import axios from 'axios';
import config from '../config';

const ANALYTICS_API_BASE = config.VITE_ANALYTICS_URL;

export type JobStatus = 'COMPLETED' | 'RUNNING' | 'FAILED' | 'PENDING' | 'IDLE';
export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy';

export interface AnalyticsJobExecution {
  id: number;
  job_type: string;
  job_name: string;
  status: JobStatus;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  records_processed: number;
  records_success: number;
  records_failed: number;
  error_message: string | null;
  triggered_by: string;
}

export interface AnalyticsJobInfo {
  job_id: string;
  name: string;
  next_run_time: string | null;
  trigger: string;
  status: JobStatus;
}

export interface AnalyticsJobHealth {
  status: HealthStatus;
  message: string;
  scheduler_running: boolean;
  recent_failures: number;
  total_executions_24h: number;
  last_checked?: string;
}

export interface TriggerResponse {
  message: string;
  execution: AnalyticsJobExecution;
}

export const analyticsJobService = {
  async listJobs(): Promise<AnalyticsJobInfo[]> {
    const response = await axios.get<AnalyticsJobInfo[]>(`${ANALYTICS_API_BASE}/admin/jobs`);
    return response.data;
  },

  async getJobStatus(jobType: string): Promise<AnalyticsJobInfo> {
    const response = await axios.get<AnalyticsJobInfo>(`${ANALYTICS_API_BASE}/admin/jobs/${jobType}`);
    return response.data;
  },

  async triggerJob(jobType: string, parameters?: Record<string, any>): Promise<TriggerResponse> {
    const response = await axios.post<TriggerResponse>(
      `${ANALYTICS_API_BASE}/admin/jobs/${jobType}/trigger`,
      parameters ? { parameters } : undefined
    );
    return response.data;
  },

  async getJobHistory(jobType: string, limit: number = 50): Promise<AnalyticsJobExecution[]> {
    const response = await axios.get<AnalyticsJobExecution[]>(
      `${ANALYTICS_API_BASE}/admin/jobs/${jobType}/history`,
      { params: { limit } }
    );
    return response.data;
  },

  async getHealth(): Promise<AnalyticsJobHealth> {
    const response = await axios.get<AnalyticsJobHealth>(`${ANALYTICS_API_BASE}/admin/jobs/health`);
    return response.data;
  },
};
